import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import deepmerge from 'deepmerge';

import { getEmployersAction, getEmployersState } from '../../../features/benefit/get_employers';
import { GET_ROUTE_EMPLOYER } from '../../../core/constants';
import { getEmployerStatus } from '../../common/utils/employer_utils';
import PaginationControls from '../../common/pagination_controls';
import { getEmployersNotificationData } from '../../../core/utils/notification_utils';
import useNotification from '../../../core/hooks/use_notification';
import { getDefaultPaginationQueryValues } from '../../../core/utils/api_utils';

const initialQueryParams = {
    ...getDefaultPaginationQueryValues
};

const EmployersList = () => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const navigate = useNavigate();

    const getEmployers = useSelector(getEmployersState);

    const [queryParams, setQueryParams] = useState(initialQueryParams);

    useEffect(() => {
        dispatch(getEmployersAction(queryParams))
            .then(response => showErrorMessage(getEmployersNotificationData(response)));
    }, [dispatch, queryParams, showErrorMessage]);

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 } }>
            { getEmployers?.loading && <LinearProgress/> }

            { getEmployers?.data?.content && (
                <Box sx={ { display: 'flex', flexDirection: 'column', gap: 2 } }>
                    <TableContainer component={ Paper } variant="outlined">
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                    <TableCell>Nazwa</TableCell>
                                    <TableCell align="center">Adres</TableCell>
                                    <TableCell align="center">NIP</TableCell>
                                    <TableCell align="center">Email</TableCell>
                                    <TableCell align="center">Status</TableCell>
                                    <TableCell align="center" sx={ { width: 100 } }></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    getEmployers?.data?.content?.map(employer => (
                                        <TableRow
                                            key={ employer?.id }
                                            hover
                                            sx={ { cursor: 'pointer' } }
                                        >
                                            <TableCell
                                                sx={ { fontWeight: 500 } }
                                                onClick={ () => navigate(GET_ROUTE_EMPLOYER(employer?.id)) }
                                            >
                                                { employer?.name }
                                            </TableCell>
                                            <TableCell align="center">
                                                { employer?.address }
                                            </TableCell>
                                            <TableCell align="center">
                                                { employer?.nip }
                                            </TableCell>
                                            <TableCell align="center">
                                                { employer?.mailingAddress }
                                            </TableCell>
                                            <TableCell align="center">
                                                { getEmployerStatus(employer?.status) }
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Edytuj">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={ (e) => {
                                                            e.stopPropagation();
                                                            navigate(`${ GET_ROUTE_EMPLOYER(employer?.id) }`);
                                                        } }
                                                    >
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                                { (!getEmployers?.data?.content || getEmployers?.data?.content.length === 0) && (
                                    <TableRow>
                                        <TableCell colSpan={ 6 } align="center"
                                                   sx={ { py: 4, color: 'text.secondary' } }>
                                            Brak wyników do wyświetlenia.
                                        </TableCell>
                                    </TableRow>
                                ) }
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box sx={ { display: 'flex', justifyContent: 'flex-end', mt: 1 } }>
                        <PaginationControls
                            currentPage={ getEmployers?.data?.pageable?.pageNumber }
                            totalPages={ getEmployers?.data?.totalPages }
                            setPage={ page => setQueryParams(prevState => deepmerge(prevState, { page })) }
                        />
                    </Box>
                </Box>
            ) }
        </Box>
    );
};

export default EmployersList;